import { useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";
import {
  Ruler,
  Shuffle,
  Check,
  X as XIcon,
  Eye,
  EyeOff,
  ArrowRight,
  RefreshCw,
  Lightbulb,
} from "@/lib/icons";
import {
  CATEGORY_LABELS,
  DA_PROBLEMS,
  type DACategory,
  type DADifficulty,
  type DAProblem,
  gradeAnswer,
  parseAnswer,
  toSigFigs,
} from "@/data/dimensionalAnalysis";

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS) as DACategory[];
const ALL_DIFFICULTIES: DADifficulty[] = ["easy", "medium", "hard"];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface AttemptState {
  problemId: string;
  studentAnswer: string;
  correct: boolean | null;
  showSteps: boolean;
}

export default function DimensionalAnalysis() {
  const [enabledCategories, setEnabledCategories] = useState<Set<DACategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [difficulty, setDifficulty] = useState<DADifficulty | "all">("all");

  const pool = useMemo(() => {
    return DA_PROBLEMS.filter(
      (p) =>
        enabledCategories.has(p.category) &&
        (difficulty === "all" || p.difficulty === difficulty),
    );
  }, [enabledCategories, difficulty]);

  const [current, setCurrent] = useState<DAProblem | null>(() =>
    DA_PROBLEMS[0] ?? null,
  );
  const [attempt, setAttempt] = useState<AttemptState>({
    problemId: DA_PROBLEMS[0]?.id ?? "",
    studentAnswer: "",
    correct: null,
    showSteps: false,
  });
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });

  const next = () => {
    if (pool.length === 0) return;
    let p = pickRandom(pool);
    // Avoid immediate repeat when possible
    if (pool.length > 1 && current && p.id === current.id) {
      p = pickRandom(pool.filter((q) => q.id !== current.id));
    }
    setCurrent(p);
    setAttempt({
      problemId: p.id,
      studentAnswer: "",
      correct: null,
      showSteps: false,
    });
  };

  const submit = () => {
    if (!current) return;
    const parsed = parseAnswer(attempt.studentAnswer);
    if (parsed === null) return;
    const ok = gradeAnswer(parsed, current);
    setAttempt((a) => ({ ...a, correct: ok, showSteps: !ok }));
    setStats((s) => ({
      attempted: s.attempted + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
  };

  const toggleCategory = (c: DACategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else next.add(c);
      return next;
    });
  };

  const accuracy =
    stats.attempted === 0 ? 0 : Math.round((stats.correct / stats.attempted) * 100);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Dimensional Analysis Practice — ElementPad" description="Step-by-step unit conversion and dimensional analysis problems with worked solutions for chemistry students." path="/tools/dimensional-analysis" />
      <AppHeader />
      <Main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
            <Ruler size={14} aria-hidden="true" />
            Skill drill
          </div>
          <h1 className="text-3xl font-bold text-foreground">Dimensional Analysis</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            The single most failed skill in high-school chemistry isn't the chemistry, it's unit
            conversion. Drill the factor-label method until it's automatic.
          </p>
        </header>

        {/* ── Lesson ──────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="lesson-heading"
          className="rounded-xl border border-border bg-tile-bg p-6 space-y-4"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-primary" aria-hidden="true" />
            <h2 id="lesson-heading" className="text-lg font-semibold text-foreground">
              The factor-label method in 4 moves
            </h2>
          </div>
          <ol className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                t: "1. Write what you have",
                d: "Start with the given value AND its units. Units are not decoration — they're the math.",
              },
              {
                t: "2. Multiply by a fraction = 1",
                d: "Every conversion factor (1 m / 100 cm) equals 1. Multiplying by 1 changes the units, not the value.",
              },
              {
                t: "3. Cancel units like algebra",
                d: "Place the unit you want to cancel in the denominator. If it doesn't cancel, you flipped the factor.",
              },
              {
                t: "4. Carry units to the answer",
                d: "The leftover unit IS your answer's unit. If it isn't what you want, add another factor.",
              },
            ].map((s) => (
              <li
                key={s.t}
                className="rounded-lg border border-border bg-background/40 p-3"
              >
                <p className="font-semibold text-foreground text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </li>
            ))}
          </ol>

          <WorkedExample
            title="9.00 g of water → molecules"
            problem="How many H₂O molecules are in 9.00 g of water? (M(H₂O) = 18.02 g/mol)"
            steps={[
              {
                label: "Step 1: Write what you have, with units",
                explain: "Always start with the given value attached to its units.",
                formula: "9.00 g H₂O",
              },
              {
                label: "Step 2: Convert grams → moles using molar mass",
                explain:
                  "Place 'g H₂O' in the denominator so the units cancel. The factor (1 mol / 18.02 g) = 1.",
                formula: "9.00 g H₂O × (1 mol H₂O / 18.02 g H₂O) = 0.4995 mol H₂O",
              },
              {
                label: "Step 3: Convert moles → molecules with Avogadro's number",
                explain: "1 mol = 6.022×10²³ particles. Put 'mol' in the denominator.",
                formula:
                  "0.4995 mol × (6.022×10²³ molecules / 1 mol) = 3.01×10²³ molecules",
              },
              {
                label: "Step 4: Check the unit cancellation",
                explain:
                  "g cancels with g, mol cancels with mol — only 'molecules' remains. ✓",
              },
            ]}
            answer="≈ 3.01×10²³ molecules of H₂O"
          />
        </section>

        {/* ── Drill lab ───────────────────────────────────────────────────── */}
        <section aria-labelledby="drill-heading" className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2
                id="drill-heading"
                className="text-lg font-semibold text-foreground"
              >
                Drill lab
              </h2>
              <p className="text-xs text-muted-foreground">
                Random problems from the categories you select.
              </p>
            </div>
            <div
              className="text-xs font-mono text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground font-semibold">{stats.correct}</span>
              /{stats.attempted} correct
              {stats.attempted > 0 && (
                <span className="ml-2">({accuracy}% accuracy)</span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl border border-border bg-tile-bg p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Difficulty
              </p>
              <div className="flex flex-wrap gap-2">
                {(["all", ...ALL_DIFFICULTIES] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      difficulty === d
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = enabledCategories.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCategory(c)}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        on
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {CATEGORY_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Drill card */}
          {current ? (
            <article className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  {CATEGORY_LABELS[current.category]} · {current.difficulty}
                </span>
                <button
                  onClick={next}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Shuffle size={12} aria-hidden="true" />
                  New problem
                </button>
              </div>

              <p className="text-base text-foreground leading-relaxed">{current.prompt}</p>

              <div className="rounded-lg border border-border bg-background/40 p-3 font-mono text-sm">
                <span className="text-muted-foreground">Given:</span>{" "}
                <span className="text-foreground font-semibold">
                  {current.givenValue} {current.givenUnit}
                </span>{" "}
                <ArrowRight
                  size={12}
                  className="inline mx-1 text-muted-foreground"
                  aria-hidden="true"
                />{" "}
                <span className="text-muted-foreground">Find:</span>{" "}
                <span className="text-foreground font-semibold">
                  ? {current.targetUnit}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <label htmlFor="da-answer" className="sr-only">
                  Your answer
                </label>
                <input
                  id="da-answer"
                  type="text"
                  inputMode="decimal"
                  value={attempt.studentAnswer}
                  onChange={(e) =>
                    setAttempt((a) => ({
                      ...a,
                      studentAnswer: e.target.value,
                      correct: null,
                    }))
                  }
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder={`e.g. ${toSigFigs(current.answer, current.sigFigs)}`}
                  className="flex-1 min-w-[180px] h-10 rounded-lg border border-input bg-tile-bg px-3 font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <span className="text-xs font-mono text-muted-foreground">
                  {current.targetUnit}
                </span>
                <button
                  onClick={submit}
                  disabled={!attempt.studentAnswer.trim()}
                  className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Check
                </button>
              </div>

              {attempt.correct !== null && (
                <div
                  role="status"
                  className={`rounded-lg border p-3 text-sm ${
                    attempt.correct
                      ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-400"
                      : "border-destructive/30 bg-destructive/5 text-destructive"
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {attempt.correct ? (
                      <Check size={14} aria-hidden="true" />
                    ) : (
                      <XIcon size={14} aria-hidden="true" />
                    )}
                    {attempt.correct
                      ? `Correct! ${toSigFigs(current.answer, current.sigFigs)} ${current.targetUnit}`
                      : `Not quite. Expected ≈ ${toSigFigs(current.answer, current.sigFigs)} ${current.targetUnit}`}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{current.insight}</p>
                </div>
              )}

              {/* Step reveal */}
              <div className="border-t border-border pt-3">
                <button
                  onClick={() =>
                    setAttempt((a) => ({ ...a, showSteps: !a.showSteps }))
                  }
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  aria-expanded={attempt.showSteps}
                >
                  {attempt.showSteps ? (
                    <EyeOff size={12} aria-hidden="true" />
                  ) : (
                    <Eye size={12} aria-hidden="true" />
                  )}
                  {attempt.showSteps ? "Hide" : "Show"} factor chain
                </button>

                {attempt.showSteps && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2 font-mono text-sm">
                      <span className="px-2 py-1 rounded bg-background border border-border">
                        {current.givenValue} {current.givenUnit}
                      </span>
                      {current.chain.map((f, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-2"
                        >
                          <span className="text-muted-foreground">×</span>
                          <span className="inline-flex flex-col items-center text-xs leading-tight px-2 py-1 rounded bg-primary/5 border border-primary/20">
                            <span className="text-foreground">{f.num}</span>
                            <span className="border-t border-border w-full my-0.5" />
                            <span className="text-muted-foreground">{f.den}</span>
                          </span>
                        </span>
                      ))}
                      <span className="text-muted-foreground">=</span>
                      <span className="px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-foreground">
                        {toSigFigs(current.answer, current.sigFigs)} {current.targetUnit}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Each fraction equals 1 — units cancel along the chain until only{" "}
                      <span className="font-mono text-foreground">{current.targetUnit}</span>{" "}
                      remains.
                    </p>
                  </div>
                )}
              </div>
            </article>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No problems match your filters. Enable a category or change difficulty.
              <button
                onClick={() => {
                  setEnabledCategories(new Set(ALL_CATEGORIES));
                  setDifficulty("all");
                }}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <RefreshCw size={12} aria-hidden="true" />
                Reset filters
              </button>
            </div>
          )}
        </section>
      </Main>
    </div>
  );
}